import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { AdapterService } from '../services/adapter.service';
import { SquareDeviceLocationService } from '../services/square-device-location.service';
import * as SquareDeviceLocationActions from './square-device-location.actions';

@Injectable()
export class SquareDeviceLocationEffects {
    private _actions$ = inject(Actions);
    private _squareDeviceLocationService = inject(SquareDeviceLocationService);
    private _alertService = inject(AlertService);
    private adapter = inject(AdapterService);

    messages = {
        unknownError: 'Unknown error',
        getOneError: 'Error getting square device location',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    getAllSquareDeviceLocations$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceLocationActions.loadSquareDeviceLocations),
            switchMap((action) =>
                this._squareDeviceLocationService.getAll(action.params).pipe(
                    map((res) => {
                        const data = res.data;
                        const adapted = this.adapter.adaptAll(data);

                        if (!adapted) {
                            throw new Error(this.messages.getOneError);
                        }

                        return SquareDeviceLocationActions.loadSquareDeviceLocationsSuccess(
                            {
                                data: adapted,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareDeviceLocationActions.loadSquareDeviceLocationsFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    getSquareDeviceLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceLocationActions.loadSquareDeviceLocation),
            switchMap((action) =>
                this._squareDeviceLocationService.getOne(action.id).pipe(
                    map((res) =>
                        SquareDeviceLocationActions.loadSquareDeviceLocationSuccess(
                            {
                                data: res.data,
                            }
                        )
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceLocationActions.loadSquareDeviceLocationFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    createSquareDeviceLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceLocationActions.createSquareDeviceLocation),
            switchMap((action) =>
                this._squareDeviceLocationService.create(action.payload).pipe(
                    map((res) => {
                        const data = res.data;
                        const adapted = this.adapter.adaptAll(data);
                        return SquareDeviceLocationActions.createSquareDeviceLocationSuccess(
                            {
                                data: adapted,
                                success:
                                    'Square device location created successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareDeviceLocationActions.createSquareDeviceLocationFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    updateSquareDeviceLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceLocationActions.updateSquareDeviceLocation),
            switchMap((action) =>
                this._squareDeviceLocationService
                    .update(action.payload, action.id)
                    .pipe(
                        map((res) => {
                            const data = res.data[0];
                            const adapted = this.adapter.adaptSingle(data);

                            if (!adapted) {
                                throw new Error(this.messages.getOneError);
                            }
                            return SquareDeviceLocationActions.updateSquareDeviceLocationSuccess(
                                {
                                    data: adapted,
                                    success:
                                        'Square device location updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                SquareDeviceLocationActions.updateSquareDeviceLocationFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    deleteSquareDeviceLocation$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceLocationActions.deleteSquareDeviceLocation),
            switchMap((action) =>
                this._squareDeviceLocationService
                    .deleteByLocation(action.locationId)
                    .pipe(
                        map(() =>
                            SquareDeviceLocationActions.deleteSquareDeviceLocationSuccess(
                                {
                                    locationId: action.locationId,
                                    success:
                                        'Square device location deleted successfully',
                                }
                            )
                        ),
                        catchError((error) =>
                            of(
                                SquareDeviceLocationActions.deleteSquareDeviceLocationFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    deleteSquareDeviceLocationItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareDeviceLocationActions.deleteSquareDeviceLocationItem),
            switchMap((action) =>
                this._squareDeviceLocationService.delete(action.id).pipe(
                    map(() =>
                        SquareDeviceLocationActions.deleteSquareDeviceLocationItemSuccess(
                            {
                                id: action.id,
                                locationId: action.locationId,
                                success:
                                    'Square device location item deleted successfully',
                            }
                        )
                    ),
                    catchError((error) =>
                        of(
                            SquareDeviceLocationActions.deleteSquareDeviceLocationItemFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareDeviceLocationActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareDeviceLocationActions.createSquareDeviceLocationSuccess,
                    SquareDeviceLocationActions.updateSquareDeviceLocationSuccess,
                    SquareDeviceLocationActions.deleteSquareDeviceLocationSuccess
                ),
                tap((action) =>
                    this._alertService.showAlert(action.success, 'success')
                )
            ),
        { dispatch: false }
    );

    onSquareDeviceLocationActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareDeviceLocationActions.loadSquareDeviceLocationsFailure,
                    SquareDeviceLocationActions.loadSquareDeviceLocationFailure,
                    SquareDeviceLocationActions.createSquareDeviceLocationFailure,
                    SquareDeviceLocationActions.updateSquareDeviceLocationFailure,
                    SquareDeviceLocationActions.deleteSquareDeviceLocationFailure
                ),
                tap((action) =>
                    this._alertService.showAlert(action.error, 'error')
                )
            ),
        { dispatch: false }
    );
}
